import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Sidebar from "../components/Sidebar";
import MobileBottomNav from "../components/MobileBottomNav";
import DashboardHeader from "../components/DashboardHeader";
import MessagePagination from "../components/MessagePagination";
import BroadcastAttachmentsList from "../components/BroadcastAttachmentsList";
import {
  Mail, Inbox, Send, Archive, Trash2, Search, Plus,
  ChevronLeft, Paperclip, X, Loader2, Circle,
  Edit3, Reply, MailOpen, AlertCircle, AlertTriangle,
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Highlighter, Undo, Redo, ArchiveRestore,
  Megaphone, Users, CheckSquare, Square, Forward, Flag, MoreHorizontal, ReplyAll
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  owner: "Director",
  admin: "Administrador",
  teacher: "Profesor",
  auxiliar: "Auxiliar",
  student: "Estudiante",
  parent: "Padre/Apoderado"
};

// ══════════════════════════════════════════════════════════════════════════════
// RICH TEXT EDITOR TOOLBAR
// ══════════════════════════════════════════════════════════════════════════════
function EditorToolbar({ editor }) {
  if (!editor) return null;

  const ToolButton = ({ onClick, isActive, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-2 rounded-lg transition-all ${
        isActive
          ? "bg-indigo-100 text-indigo-700"
          : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
      }`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-6 bg-gray-200 mx-1" />;

  return (
    <div className="flex items-center flex-wrap gap-0.5 p-2 border-b border-gray-200 bg-gray-50 rounded-t-xl">
      <ToolButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer"><Undo className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer"><Redo className="w-4 h-4" /></ToolButton>
      <Divider />
      <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} title="Negrita"><Bold className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} title="Cursiva"><Italic className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive("underline")} title="Subrayado"><UnderlineIcon className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive("strike")} title="Tachado"><Strikethrough className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().toggleHighlight().run()} isActive={editor.isActive("highlight")} title="Resaltar"><Highlighter className="w-4 h-4" /></ToolButton>
      <Divider />
      <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} title="Lista"><List className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} title="Lista numerada"><ListOrdered className="w-4 h-4" /></ToolButton>
      <Divider />
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} title="Alinear izquierda"><AlignLeft className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} title="Centrar"><AlignCenter className="w-4 h-4" /></ToolButton>
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} title="Alinear derecha"><AlignRight className="w-4 h-4" /></ToolButton>
      <Divider />
      <ToolButton
        onClick={() => {
          const url = window.prompt("URL del enlace:");
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        isActive={editor.isActive("link")}
        title="Enlace"
      ><LinkIcon className="w-4 h-4" /></ToolButton>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSE MODAL
// ══════════════════════════════════════════════════════════════════════════════
function ComposeModal({ isOpen, onClose, token, onSent, replyTo, user }) {
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);

  // Broadcast state
  const [canBroadcast, setCanBroadcast] = useState(false);
  const [isBroadcast, setIsBroadcast] = useState(false);
  const [broadcastRoles, setBroadcastRoles] = useState({ teacher: false, student: false, parent: false, admin: false });
  const [recipientCounts, setRecipientCounts] = useState({ teacher: 0, student: 0, parent: 0, admin: 0 });
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Attachments (Drive-backed). Only available in broadcast mode.
  const [driveConnected, setDriveConnected] = useState(null); // null=unknown, true/false=loaded
  const [attachments, setAttachments] = useState([]); // [{file_id, name, mime_type, size, drive_file_id}]
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const BROADCAST_ROLE_LABELS = {
    teacher: "Profesores",
    student: "Alumnos",
    parent: "Padres",
    admin: "Administradores"
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Highlight,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: "Escribe tu mensaje aquí..." }),
    ],
    content: "",
    editorProps: {
      attributes: { class: "prose prose-sm max-w-none focus:outline-none min-h-[200px] p-4" },
    },
  });

  // Check broadcast permission on open
  useEffect(() => {
    if (!isOpen || !token) return;
    const checkPermission = async () => {
      try {
        const res = await axios.get(`${API}/api/broadcast/permission`, { headers });
        setCanBroadcast(res.data.can_send_broadcast || false);
      } catch {
        setCanBroadcast(false);
      }
    };
    checkPermission();
  }, [isOpen, token]);

  // Load drive connection status when broadcast mode enabled.
  useEffect(() => {
    if (!isBroadcast || !token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/messaging/drive-status`, { headers });
        setDriveConnected(Boolean(res.data?.connected && res.data?.materials_folder_configured));
      } catch {
        setDriveConnected(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroadcast, token]);

  // Load recipient counts when broadcast mode enabled
  useEffect(() => {
    if (!isBroadcast || !token) return;
    const loadCounts = async () => {
      setLoadingCounts(true);
      try {
        const res = await axios.get(`${API}/api/broadcast/recipients-count`, { headers });
        setRecipientCounts(res.data.counts || {});
      } catch (err) {
        console.error("Error loading counts:", err);
      } finally {
        setLoadingCounts(false);
      }
    };
    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBroadcast, token]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleAttachClick = () => {
    if (driveConnected === false) {
      setError("Google Drive no está conectado. Ve a Ajustes → Google Drive para conectarlo antes de adjuntar archivos.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file later
    if (!files.length) return;
    setError("");
    for (const file of files) {
      if (file.size > 200 * 1024 * 1024) {
        setError(`"${file.name}" supera el límite de 200 MB.`);
        continue;
      }
      setUploadingFile(true);
      try {
        const form = new FormData();
        form.append("file", file);
        const res = await axios.post(`${API}/api/messaging/attachments/upload`, form, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
        });
        setAttachments(prev => [...prev, res.data]);
      } catch (err) {
        setError(err.response?.data?.detail || `Error subiendo "${file.name}"`);
        break;
      } finally {
        setUploadingFile(false);
      }
    }
  };

  const removeAttachment = (file_id) => {
    setAttachments(prev => prev.filter(a => a.file_id !== file_id));
  };

  useEffect(() => {
    if (replyTo) {
      setSubject(replyTo.subject.startsWith("Re:") ? replyTo.subject : `Re: ${replyTo.subject}`);
      setRecipients([replyTo.sender]);
    } else {
      setSubject("");
      setRecipients([]);
    }
    if (editor) editor.commands.setContent("");
    setError("");
    setIsBroadcast(false);
    setBroadcastRoles({ teacher: false, student: false, parent: false, admin: false });
    setAttachments([]);
    setDriveConnected(null);
  }, [replyTo, isOpen, editor]);

  const searchContacts = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/api/internal-mail/contacts/search?q=${encodeURIComponent(query)}`, { headers });
      setSearchResults(res.data.contacts || []);
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => searchContacts(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addRecipient = (contact) => {
    if (!recipients.find(r => r.id === contact.id)) {
      const fullName = contact.last_name
        ? `${contact.name || contact.first_name || ''} ${contact.last_name}`.trim()
        : contact.name || contact.first_name || '';
      setRecipients([...recipients, { ...contact, name: fullName }]);
    }
    setSearchQuery("");
    setSearchResults([]);
    setShowRecipientDropdown(false);
  };

  const removeRecipient = (id) => setRecipients(recipients.filter(r => r.id !== id));

  const toggleBroadcastRole = (role) => {
    setBroadcastRoles(prev => ({ ...prev, [role]: !prev[role] }));
  };

  const selectedBroadcastRoles = Object.entries(broadcastRoles).filter(([, v]) => v).map(([k]) => k);
  const broadcastTotal = selectedBroadcastRoles.reduce((sum, r) => sum + (recipientCounts[r] || 0), 0);

  const handleSend = async () => {
    if (!subject.trim()) return setError("El asunto es requerido");
    const bodyContent = editor?.getHTML() || "";
    const bodyText = editor?.getText() || "";
    if (!bodyText.trim()) return setError("El mensaje no puede estar vacio");

    // Broadcast mode
    if (isBroadcast && selectedBroadcastRoles.length > 0) {
      setSending(true);
      setError("");
      try {
        await axios.post(`${API}/api/broadcast/send`, {
          subject: subject.trim(),
          body: bodyContent,
          target_roles: selectedBroadcastRoles,
          attachments,
        }, { headers });
        onSent?.(true); // true = was broadcast
        onClose();
      } catch (err) {
        setError(err.response?.data?.detail || "Error al enviar el comunicado");
      } finally {
        setSending(false);
      }
      return;
    }

    // Normal message mode
    if (recipients.length === 0) return setError("Selecciona al menos un destinatario");

    setSending(true);
    setError("");

    try {
      if (replyTo) {
        await axios.post(`${API}/api/internal-mail/${replyTo.id}/reply`, { body: bodyContent }, { headers });
      } else {
        await axios.post(`${API}/api/internal-mail/send`, {
          subject: subject.trim(),
          body: bodyContent,
          recipient_ids: recipients.map(r => r.id)
        }, { headers });
      }
      onSent?.(false); // false = regular message
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al enviar el mensaje");
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className={`${isBroadcast && selectedBroadcastRoles.length > 0 ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-indigo-600 to-purple-600'} px-6 py-4 flex items-center justify-between flex-shrink-0 transition-colors`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              {isBroadcast && selectedBroadcastRoles.length > 0 ? (
                <Megaphone className="w-5 h-5 text-white" />
              ) : (
                <Edit3 className="w-5 h-5 text-white" />
              )}
            </div>
            <h2 className="text-lg font-semibold text-white">
              {isBroadcast && selectedBroadcastRoles.length > 0 ? "Comunicado Institucional" : replyTo ? "Responder mensaje" : "Nuevo mensaje"}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />{error}
            </div>
          )}

          {/* Para: field (hidden when broadcast is active with roles selected) */}
          {!(isBroadcast && selectedBroadcastRoles.length > 0) && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Para:</label>
              <div className="relative">
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl min-h-[48px]">
                  {recipients.map(r => (
                    <span key={r.id} className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm">
                      {r.name}
                      <button onClick={() => removeRecipient(r.id)} className="hover:text-indigo-900"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setShowRecipientDropdown(true); }}
                    onFocus={() => setShowRecipientDropdown(true)}
                    placeholder={recipients.length === 0 ? "Buscar destinatarios..." : ""}
                    className="flex-1 min-w-[150px] bg-transparent focus:outline-none text-sm"
                    data-testid="compose-recipient-search"
                  />
                </div>

                {showRecipientDropdown && (searchResults.length > 0 || searching) && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto">
                    {searching ? (
                      <div className="p-3 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
                    ) : (
                      searchResults.map(contact => {
                        const fullName = contact.last_name
                          ? `${contact.name || contact.first_name || ''} ${contact.last_name}`.trim()
                          : contact.name || contact.first_name || '';
                        return (
                          <button
                            key={contact.id}
                            onClick={() => addRecipient(contact)}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                          >
                            {contact.photo_url ? (
                              <img src={contact.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                                {fullName?.charAt(0)}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">{fullName}</p>
                              <p className="text-xs text-gray-500">{ROLE_LABELS[contact.role] || contact.role}</p>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Broadcast Section - Only visible for authorized users */}
          {canBroadcast && !replyTo && (
            <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-4" data-testid="broadcast-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Comunicado Institucional</span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsBroadcast(!isBroadcast)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${isBroadcast ? 'bg-amber-500' : 'bg-gray-300'}`}
                  data-testid="broadcast-toggle"
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isBroadcast ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              {isBroadcast && (
                <div className="space-y-3 mt-3 pt-3 border-t border-amber-200">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Destinatarios del comunicado</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(BROADCAST_ROLE_LABELS).map(([role, label]) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleBroadcastRole(role)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 transition-all text-left ${
                          broadcastRoles[role]
                            ? 'border-amber-500 bg-amber-100 text-amber-800'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                        data-testid={`broadcast-role-${role}`}
                      >
                        {broadcastRoles[role] ? (
                          <CheckSquare className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{label}</p>
                          {loadingCounts ? (
                            <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                          ) : (
                            <p className="text-xs text-gray-400">{recipientCounts[role] || 0} usuarios</p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Recipient preview */}
                  {selectedBroadcastRoles.length > 0 && (
                    <div className="bg-white border border-amber-200 rounded-lg p-3 mt-2" data-testid="broadcast-preview">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Este comunicado sera enviado a:</p>
                      {selectedBroadcastRoles.map(role => (
                        <p key={role} className="text-sm text-amber-700 flex items-center gap-1.5">
                          <CheckSquare className="w-3.5 h-3.5" />
                          {recipientCounts[role] || 0} {BROADCAST_ROLE_LABELS[role]?.toLowerCase()}
                        </p>
                      ))}
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="text-sm font-bold text-gray-800">
                          Total destinatarios: {broadcastTotal.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Asunto:</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Escribe el asunto..."
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="compose-subject"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Mensaje:</label>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-indigo-500">
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} className="min-h-[200px] max-h-[300px] overflow-y-auto" />
            </div>
          </div>

          {/* Drive-backed attachments (broadcast mode only) */}
          {isBroadcast && selectedBroadcastRoles.length > 0 && (
            <div data-testid="compose-attachments-section">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Archivos adjuntos:</label>

              {driveConnected === false && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="drive-not-connected-warning">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-1">Google Drive no está conectado</p>
                    <p className="text-xs">Para adjuntar archivos a comunicados, conecta el Drive del colegio en <b>Ajustes → Google Drive</b>. Los archivos se almacenan en la carpeta "Comunicados" del Drive institucional.</p>
                  </div>
                </div>
              )}

              {attachments.length > 0 && (
                <ul className="space-y-1.5 mt-2" data-testid="compose-attachments-list">
                  {attachments.map(att => (
                    <li key={att.file_id} className="flex items-center gap-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg text-sm">
                      <Paperclip className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium text-gray-800">{att.name}</div>
                        <div className="text-xs text-gray-500">{formatFileSize(att.size)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(att.file_id)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Quitar"
                        data-testid={`remove-attachment-${att.file_id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between flex-shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*,video/*,audio/*,text/*,.zip"
            onChange={handleFileSelected}
            className="hidden"
            data-testid="compose-file-input"
          />
          <button
            onClick={handleAttachClick}
            disabled={!isBroadcast || selectedBroadcastRoles.length === 0 || uploadingFile || driveConnected === false}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={!isBroadcast ? "Disponible solo para comunicados institucionales"
              : driveConnected === false ? "Drive no conectado"
              : "Adjuntar archivos al comunicado"}
            data-testid="compose-attach-btn"
          >
            {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            {uploadingFile ? "Subiendo..." : "Adjuntar"}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">Cancelar</button>
            <button
              onClick={handleSend}
              disabled={sending}
              className={`px-6 py-2 ${isBroadcast && selectedBroadcastRoles.length > 0 ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'} text-white font-semibold rounded-lg flex items-center gap-2 transition-all disabled:opacity-50`}
              data-testid="compose-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : isBroadcast && selectedBroadcastRoles.length > 0 ? <Megaphone className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {isBroadcast && selectedBroadcastRoles.length > 0 ? "Enviar Comunicado" : "Enviar"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CONFIRM MODAL
// ══════════════════════════════════════════════════════════════════════════════
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, confirmText, confirmStyle, icon: Icon, loading }) {
  if (!isOpen) return null;

  const styleClasses = {
    danger: "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700",
    warning: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600",
    primary: "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700",
  };

  const iconBgClasses = {
    danger: "bg-red-100 text-red-600",
    warning: "bg-amber-100 text-amber-600",
    primary: "bg-indigo-100 text-indigo-600",
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBgClasses[confirmStyle] || iconBgClasses.danger}`}>
              {Icon && <Icon className="w-6 h-6" />}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">{message}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex items-center justify-end gap-3">
          <button onClick={onClose} disabled={loading} className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50">Cancelar</button>
          <button onClick={onConfirm} disabled={loading} className={`px-5 py-2.5 text-white font-semibold rounded-xl flex items-center gap-2 transition-all disabled:opacity-50 ${styleClasses[confirmStyle] || styleClasses.danger}`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN MESSAGES PAGE (Owner/Admin Portal)
// ══════════════════════════════════════════════════════════════════════════════
export default function MessagesPage({ user, token, subdomain, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFolder, setActiveFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ unread: 0, inbox: 0, sent: 0, archived: 0, trash: 0 });
  const [showCompose, setShowCompose] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileView, setMobileView] = useState("list");
  const [settings, setSettings] = useState(null);

  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null, messageId: null });
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);

  // Broadcast state
  const [broadcastMessages, setBroadcastMessages] = useState([]);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [broadcastStats, setBroadcastStats] = useState(null);
  const [broadcastStatsLoading, setBroadcastStatsLoading] = useState(false);
  const [canBroadcast, setCanBroadcast] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const schoolName = settings?.system_name || user?.school_name || "Mi Colegio";
  const logoUrl = settings?.logo_url;

  const folders = [
    { id: "inbox", label: "Bandeja de entrada", icon: Inbox, badge: stats.unread },
    { id: "sent", label: "Enviados", icon: Send },
    { id: "broadcasts", label: "Comunicados", icon: Megaphone },
    { id: "archived", label: "Archivados", icon: Archive },
    { id: "trash", label: "Papelera", icon: Trash2 },
  ];

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/api/settings`, { headers });
      setSettings(res.data);
    } catch (err) {
      console.error("Error loading settings:", err);
    }
  };

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/api/internal-mail/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const loadMessages = async (folder, page = 1) => {
    if (folder === "broadcasts") {
      setLoading(true);
      try {
        const endpoint = canBroadcast ? `${API}/api/broadcast/sent` : `${API}/api/broadcast/inbox`;
        const res = await axios.get(`${endpoint}?page=${page}&limit=6`, { headers });
        setBroadcastMessages(res.data.broadcasts || []);
        setTotalMessages(res.data.total || res.data.broadcasts?.length || 0);
        setTotalPages(res.data.pages || 1);
        setCurrentPage(page);
      } catch (err) {
        console.error("Error loading broadcasts:", err);
        setBroadcastMessages([]);
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/internal-mail/${folder}?page=${page}&limit=6`, { headers });
      setMessages(res.data.messages || []);
      setTotalMessages(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
      setCurrentPage(page);
    } catch (err) {
      console.error("Error loading messages:", err);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMessage = async (messageId) => {
    try {
      const res = await axios.get(`${API}/api/internal-mail/${messageId}`, { headers });
      setSelectedMessage(res.data);
      // Immediately update local list to show as read
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true } : m));
      loadStats();
    } catch (err) {
      console.error("Error loading message:", err);
    }
  };

  useEffect(() => {
    loadStats();
    loadSettings();
    axios.get(`${API}/api/broadcast/permission`, { headers })
      .then(res => setCanBroadcast(res.data.can_send_broadcast || false))
      .catch(() => setCanBroadcast(false));
  }, [token]);

  useEffect(() => {
    loadMessages(activeFolder, 1);
    setSelectedMessage(null);
    setSelectedBroadcast(null);
    setBroadcastStats(null);
    setCurrentPage(1);
  }, [activeFolder]);

  const handleSelectMessage = (msg) => {
    if (msg.message_type === "broadcast") {
      handleSelectBroadcast(msg);
    } else {
      loadMessage(msg.id);
      setMobileView("message");
    }
  };

  const handleArchive = async (messageId) => {
    try {
      await axios.put(`${API}/api/internal-mail/${messageId}/archive`, null, { headers });
      loadMessages(activeFolder);
      loadStats();
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
    } catch (err) { console.error("Error archiving:", err); }
  };

  const handleDelete = async (messageId) => {
    try {
      await axios.delete(`${API}/api/internal-mail/${messageId}`, { headers });
      loadMessages(activeFolder);
      loadStats();
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
    } catch (err) { console.error("Error deleting:", err); }
  };

  const showDeletePermanentlyConfirm = (messageId) => {
    setConfirmModal({ isOpen: true, type: "deletePermanently", messageId });
  };

  const showEmptyTrashConfirm = () => {
    setConfirmModal({ isOpen: true, type: "emptyTrash", messageId: null });
  };

  const handleConfirmAction = async () => {
    setConfirmLoading(true);
    try {
      if (confirmModal.type === "deletePermanently" && confirmModal.messageId) {
        await axios.delete(`${API}/api/internal-mail/${confirmModal.messageId}/permanent`, { headers });
        if (selectedMessage?.id === confirmModal.messageId) setSelectedMessage(null);
      } else if (confirmModal.type === "emptyTrash") {
        await axios.delete(`${API}/api/internal-mail/trash/empty`, { headers });
        setSelectedMessage(null);
      }
      loadMessages(activeFolder);
      loadStats();
      setConfirmModal({ isOpen: false, type: null, messageId: null });
    } catch (err) { console.error("Error in confirm action:", err); } finally { setConfirmLoading(false); }
  };

  const handleRestore = async (messageId) => {
    try {
      await axios.put(`${API}/api/internal-mail/${messageId}/restore`, null, { headers });
      loadMessages(activeFolder);
      loadStats();
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
    } catch (err) { console.error("Error restoring:", err); }
  };

  const handleToggleRead = async (messageId, isRead) => {
    try {
      await axios.put(`${API}/api/internal-mail/${messageId}/read?is_read=${isRead}`, null, { headers });
      loadMessages(activeFolder);
      loadStats();
    } catch (err) { console.error("Error toggling read:", err); }
  };

  const handleReply = () => {
    if (selectedMessage) {
      setReplyTo(selectedMessage);
      setShowCompose(true);
    }
  };

  const handleSelectBroadcast = async (broadcast) => {
    setSelectedBroadcast(broadcast);
    setSelectedMessage(null);
    setMobileView("message");
    // If sender, load stats
    if (canBroadcast) {
      setBroadcastStatsLoading(true);
      try {
        const res = await axios.get(`${API}/api/broadcast/${broadcast.id}/stats`, { headers });
        setBroadcastStats(res.data);
      } catch (err) {
        console.error("Error loading stats:", err);
        setBroadcastStats(null);
      } finally {
        setBroadcastStatsLoading(false);
      }
    } else {
      // Mark as read for receiver
      try {
        await axios.post(`${API}/api/broadcast/${broadcast.id}/read`, {}, { headers });
        setBroadcastMessages(prev => prev.map(b => b.id === broadcast.id ? { ...b, is_read: true } : b));
      } catch (err) {
        console.error("Error marking broadcast read:", err);
      }
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return date.toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
    return date.toLocaleDateString("es-PE", { day: "numeric", month: "short" });
  };

  const filteredMessages = messages.filter(msg => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return msg.subject?.toLowerCase().includes(q) || msg.sender?.name?.toLowerCase().includes(q) || msg.body_preview?.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="owner-messages-page">
      <Sidebar
        user={user}
        onLogout={onLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        subdomain={subdomain}
        schoolName={schoolName}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <DashboardHeader
          user={user}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          onLogout={onLogout}
          logoUrl={logoUrl}
          schoolName={schoolName}
        />

        {/* Main Content */}
        <main className="flex-1 flex flex-col lg:flex-row overflow-hidden pb-20 lg:pb-0">
          {/* Folders Sidebar */}
          <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200 flex-shrink-0">
            <div className="p-4">
              <button
                onClick={() => { setReplyTo(null); setShowCompose(true); }}
                className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
                data-testid="compose-btn"
              >
                <Plus className="w-5 h-5" />
                Redactar
              </button>
            </div>

            <nav className="flex-1 px-3 py-2 space-y-1">
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => setActiveFolder(folder.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeFolder === folder.id ? "bg-indigo-50 text-indigo-700" : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-testid={`folder-${folder.id}`}
                >
                  <folder.icon className="w-5 h-5" />
                  <span className="flex-1 text-left font-medium">{folder.label}</span>
                  {folder.badge > 0 && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{folder.badge}</span>
                  )}
                </button>
              ))}

              {activeFolder === "trash" && stats.trash > 0 && (
                <button
                  onClick={showEmptyTrashConfirm}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all border border-red-200"
                  data-testid="empty-trash-btn"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium text-sm">Vaciar papelera</span>
                </button>
              )}
            </nav>
          </aside>

          {/* Message List */}
          <div className={`flex-1 flex flex-col lg:max-w-md border-r border-gray-200 bg-white ${mobileView === "message" ? "hidden lg:flex" : "flex"}`}>
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar mensajes..."
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-100 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all"
                  data-testid="search-messages"
                />
              </div>

              {/* Mobile folder selector */}
              <div className="lg:hidden mt-3 flex gap-2 overflow-x-auto pb-2">
                {folders.map(folder => (
                  <button
                    key={folder.id}
                    onClick={() => setActiveFolder(folder.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap text-sm transition-all ${
                      activeFolder === folder.id ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    <folder.icon className="w-4 h-4" />
                    {folder.label}
                    {folder.badge > 0 && (
                      <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{folder.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                </div>
              ) : activeFolder === "broadcasts" ? (
                broadcastMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                    <Megaphone className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No hay comunicados</p>
                    <p className="text-sm">Los comunicados institucionales apareceran aquí</p>
                  </div>
                ) : (
                  broadcastMessages.map(b => (
                    <button
                      key={b.id}
                      onClick={() => handleSelectBroadcast(b)}
                      className={`w-full p-4 border-b border-gray-100 text-left transition-all hover:bg-amber-50/50 ${
                        selectedBroadcast?.id === b.id ? "bg-amber-50" : ""
                      } ${b.is_read === false ? "bg-amber-50/30" : ""}`}
                      data-testid={`broadcast-${b.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
                          <Megaphone className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded uppercase flex-shrink-0">Comunicado</span>
                              <p className={`text-sm truncate ${b.is_read === false ? "font-bold text-gray-900" : "font-normal text-gray-700"}`}>{b.sender_name || "Propietario"}</p>
                            </div>
                            <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(b.created_at)}</span>
                          </div>
                          <p className={`text-sm truncate ${b.is_read === false ? "font-semibold text-gray-800" : "font-normal text-gray-600"}`}>{b.subject}</p>
                          {canBroadcast && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-400">{b.total_recipients} destinatarios</span>
                              <span className="text-xs text-emerald-600">{b.read_count || 0} leidos</span>
                            </div>
                          )}
                        </div>
                        {b.is_read === false && (
                          <Circle className="w-2 h-2 fill-amber-500 text-amber-500 flex-shrink-0 mt-2" />
                        )}
                      </div>
                    </button>
                  ))
                )
              ) : filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                  <Mail className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No hay mensajes</p>
                  <p className="text-sm">Esta carpeta esta vacia</p>
                </div>
              ) : (
                filteredMessages.map(msg => {
                  const isRead = activeFolder === "sent" || activeFolder === "trash" || msg.is_read;
                  return (
                  <button
                    key={msg.id}
                    onClick={() => handleSelectMessage(msg)}
                    className={`w-full p-4 border-b border-gray-100 text-left transition-all hover:bg-gray-50 ${
                      selectedMessage?.id === msg.id ? (msg.message_type === "broadcast" ? "bg-amber-50" : "bg-indigo-50") : ""
                    } ${!isRead ? (msg.message_type === "broadcast" ? "bg-amber-50/30" : "bg-blue-50/50") : ""}`}
                    data-testid={`message-${msg.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {msg.message_type === "broadcast" ? (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white flex-shrink-0">
                          <Megaphone className="w-5 h-5" />
                        </div>
                      ) : activeFolder === "sent" ? (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {msg.recipients?.[0]?.name?.charAt(0) || "?"}
                        </div>
                      ) : msg.sender?.photo_url ? (
                        <img src={msg.sender.photo_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                          {msg.sender?.name?.charAt(0) || "?"}
                        </div>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {msg.message_type === "broadcast" && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded uppercase flex-shrink-0">Comunicado</span>
                            )}
                            <p className={`text-sm truncate ${!isRead ? "font-bold text-gray-900" : "font-normal text-gray-700"}`}>
                              {activeFolder === "sent"
                                ? (msg.recipients?.map(r => r.name).join(", ") || "Sin destinatarios")
                                : msg.sender?.name || "Remitente desconocido"
                              }
                            </p>
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(msg.created_at)}</span>
                        </div>
                        <p className={`text-sm truncate ${!isRead ? "font-semibold text-gray-800" : "font-normal text-gray-600"}`}>
                          {msg.subject}
                        </p>
                        <p className={`text-xs truncate mt-0.5 ${!isRead ? "font-medium text-gray-500" : "font-normal text-gray-400"}`}>{stripHtml(msg.body_preview)}</p>
                      </div>

                      <div className="flex flex-col items-center gap-1">
                        {!isRead && <Circle className={`w-2 h-2 ${msg.message_type === "broadcast" ? "fill-amber-500 text-amber-500" : "fill-blue-500 text-blue-500"}`} />}
                        {msg.has_attachments && <Paperclip className="w-3 h-3 text-gray-400" />}
                      </div>
                    </div>
                  </button>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            <MessagePagination
              page={currentPage}
              totalPages={totalPages}
              totalMessages={totalMessages}
              onPageChange={(p) => loadMessages(activeFolder, p)}
            />

            {/* Mobile Compose */}
            <div className="lg:hidden p-4 border-t bg-white">
              <button
                onClick={() => { setReplyTo(null); setShowCompose(true); }}
                className="w-full py-3 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Redactar
              </button>
            </div>
          </div>

          {/* Message Detail */}
          <div className={`flex-1 flex flex-col bg-white ${mobileView === "list" ? "hidden lg:flex" : "flex"}`}>
            {selectedBroadcast ? (
              <>
                <div className="p-6 border-b border-gray-100">
                  <button
                    onClick={() => { setSelectedBroadcast(null); setMobileView("list"); }}
                    className="lg:hidden flex items-center gap-2 text-gray-600 mb-4"
                  >
                    <ChevronLeft className="w-5 h-5" />Volver
                  </button>

                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-lg uppercase flex items-center gap-1">
                      <Megaphone className="w-3.5 h-3.5" /> Comunicado Institucional
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedBroadcast.subject}</h2>

                  <div className="flex items-center gap-4 mt-4">
                    {selectedBroadcast.sender_photo ? (
                      <img src={selectedBroadcast.sender_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold">
                        <Megaphone className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{selectedBroadcast.sender_name}</p>
                      <p className="text-sm text-gray-500 capitalize">
                        Enviado a: {selectedBroadcast.target_roles?.map(r => 
                          r === "teacher" ? "Profesores" : r === "student" ? "Alumnos" : r === "parent" ? "Padres" : "Administradores"
                        ).join(", ")}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {new Date(selectedBroadcast.created_at).toLocaleDateString("es-PE", {
                          weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Stats for sender */}
                  {canBroadcast && broadcastStats && (
                    <div className="mt-4 grid grid-cols-3 gap-3" data-testid="broadcast-stats">
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-slate-700">{broadcastStats.total}</p>
                        <p className="text-xs text-slate-500">Enviados</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{broadcastStats.read}</p>
                        <p className="text-xs text-emerald-600">Leidos</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-amber-600">{broadcastStats.pending}</p>
                        <p className="text-xs text-amber-600">Pendientes</p>
                      </div>
                    </div>
                  )}
                  {canBroadcast && broadcastStatsLoading && (
                    <div className="mt-4 flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: selectedBroadcast.body }} />
                </div>
              </>
            ) : selectedMessage ? (
              <>
                {/* Toolbar de acciones */}
                <div className="flex items-center gap-1 px-4 py-2 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                  <button
                    onClick={() => { setSelectedMessage(null); setMobileView("list"); }}
                    className="lg:hidden p-2 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors mr-1"
                    data-testid="back-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  <button onClick={handleReply} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" title="Responder" data-testid="reply-btn">
                    <Reply className="w-4 h-4" />
                    <span className="hidden sm:inline">Responder</span>
                  </button>
                  <button onClick={handleReply} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" title="Responder a todos" data-testid="reply-all-btn">
                    <ReplyAll className="w-4 h-4" />
                    <span className="hidden sm:inline">Responder a todos</span>
                  </button>
                  <button onClick={() => { setReplyTo({...selectedMessage, forwardMode: true}); setShowCompose(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" title="Reenviar" data-testid="forward-btn">
                    <Forward className="w-4 h-4" />
                    <span className="hidden sm:inline">Reenviar</span>
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1" />

                  {activeFolder === "trash" ? (
                    <>
                      <button onClick={() => handleRestore(selectedMessage.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Restaurar" data-testid="restore-btn">
                        <ArchiveRestore className="w-4 h-4" />
                        <span className="hidden sm:inline">Restaurar</span>
                      </button>
                      <button onClick={() => showDeletePermanentlyConfirm(selectedMessage.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar permanentemente" data-testid="delete-permanent-btn">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleDelete(selectedMessage.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors" title="Eliminar" data-testid="delete-btn">
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Eliminar</span>
                      </button>
                      <button onClick={() => handleArchive(selectedMessage.id)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" title="Archivar" data-testid="archive-btn">
                        <Archive className="w-4 h-4" />
                        <span className="hidden sm:inline">Archivar</span>
                      </button>
                    </>
                  )}

                  <div className="w-px h-6 bg-gray-300 mx-1" />

                  <button onClick={() => handleToggleRead(selectedMessage.id, !selectedMessage.is_read)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors" title={selectedMessage.is_read ? "Marcar como no leido" : "Marcar como leido"} data-testid="mark-read-btn">
                    <Flag className="w-4 h-4" />
                    <span className="hidden sm:inline">Marcar</span>
                  </button>
                </div>

                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start justify-between gap-4">
                    <h2 className="text-xl font-bold text-gray-900">{selectedMessage.subject}</h2>
                  </div>

                  <div className="flex items-center gap-4 mt-4">
                    {selectedMessage.sender?.photo_url ? (
                      <img src={selectedMessage.sender.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-lg font-bold">
                        {selectedMessage.sender?.name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{selectedMessage.sender?.name}</p>
                      <p className="text-sm text-gray-500">{selectedMessage.sender?.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {new Date(selectedMessage.created_at).toLocaleDateString("es-PE", {
                          weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit"
                        })}
                      </p>
                    </div>
                  </div>

                  {selectedMessage.recipients?.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                      <span>Para:</span>
                      <span>{selectedMessage.recipients.map(r => r.name).join(", ")}</span>
                    </div>
                  )}

                  {/* Read stats for sent messages */}
                  {selectedMessage.read_stats && (
                    <div className="mt-4 grid grid-cols-3 gap-3" data-testid="message-read-stats">
                      <div className="bg-slate-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-slate-700">{selectedMessage.read_stats.total}</p>
                        <p className="text-xs text-slate-500">Enviados</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-emerald-600">{selectedMessage.read_stats.read}</p>
                        <p className="text-xs text-emerald-600">Leidos</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-3 text-center">
                        <p className="text-2xl font-bold text-amber-600">{selectedMessage.read_stats.pending}</p>
                        <p className="text-xs text-amber-600">Pendientes</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: selectedMessage.body }} />

                  <BroadcastAttachmentsList message={selectedMessage} token={token} />
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
                <Mail className="w-20 h-20 mb-4 opacity-30" />
                <p className="text-xl font-medium">Selecciona un mensaje</p>
                <p className="text-sm mt-1">Haz clic en un mensaje para ver su contenido</p>
              </div>
            )}
          </div>
        </main>
      </div>

      <ComposeModal
        isOpen={showCompose}
        onClose={() => { setShowCompose(false); setReplyTo(null); }}
        token={token}
        onSent={(wasBroadcast) => {
          if (wasBroadcast) {
            setActiveFolder("broadcasts");
          } else {
            loadMessages(activeFolder);
          }
          loadStats();
        }}
        replyTo={replyTo}
        user={user}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: null, messageId: null })}
        onConfirm={handleConfirmAction}
        title={confirmModal.type === "emptyTrash" ? "Vaciar papelera" : "Eliminar mensaje"}
        message={
          confirmModal.type === "emptyTrash"
            ? "Se eliminaran todos los mensajes de forma permanente y esta acción no se puede deshacer."
            : "Se eliminara este mensaje permanentemente. Esta acción no se puede deshacer."
        }
        confirmText={confirmModal.type === "emptyTrash" ? "Vaciar papelera" : "Eliminar"}
        confirmStyle="danger"
        icon={AlertTriangle}
        loading={confirmLoading}
      />
      <MobileBottomNav role={user?.role === "admin" ? "admin" : "owner"} />
    </div>
  );
}
